import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import DenotedGardenSectionMenu from './DenotedGardenSectionMenu';
import Tutorial from './tutorialComponents/TutorialPopup';
import MainPageTutorialFirst from "./tutorialComponents/MainPageTutorialFirst";
import MainPageTutorialSecond from "./tutorialComponents/MainPageTutorialSecond";
import {
    PencilLine,
    X,
    Eraser,
    Trash,
    LayoutDashboard,
    PaintBucket,
    Move,
    ChevronsUp,
    ChevronsDown,
    ChevronsLeft,
    ChevronsRight,
    ArrowLeft,
    Info,
    Grid3x3
} from 'lucide-react';
import './GardenStyling.css';
import { useNavigate } from "react-router-dom";
import RibbonNav from "./RibbonNav";
import CollapsibleSection from "./CollapsibleSection";
import HorizontalCollapsibleSection from "./HorizontalCollapsibleSection";
import {useAuth} from "../hooks/useAuth";



const InGroundGardenDesigner = ({ onBack }) => {
    const [activeTool, setActiveTool] = useState('add'); // Track active button

    const [inputWidth, setInputWidth] = useState(25);
    const [inputHeight, setInputHeight] = useState(25);

    const [width, setWidth] = useState(25);
    const [height, setHeight] = useState(25);
    const totalCells = width * height;
    const [gridColors, setGridColors] = useState(Array(totalCells).fill(false));
    const [isDragging, setIsDragging] = useState(false);

    const navigate = useNavigate();

    // Stores selected cells for movement with move tool
    const [selectedCells, setSelectedCells] = useState([]);

    // for movement controls animation when user selects move tool
    const [showMoveControls, setShowMoveControls] = useState(false);

    // For helping to denote the garden
    const [denoteActive, setDenoteActive] = useState(false); // for showing the denote screen
    const [denotedPlots, setDenotedPlots] = useState({});
    const [isNaming, setIsNaming] = useState(false);
    const [isNamingDuplicatedGarden, setIsNamingDuplicatedGarden] = useState(false);
    const [currentPlot, setCurrentPlot] = useState(null);
    const [gardenName, setGardenName] = useState('');

    //for saved, denoted garden plots, contains all necessary info related to the garden EXCEPT PLANTS PLANTED
    const [savedGardenPlots, setSavedGardenPlots] = useState({});

    // different vars for whatever screen size it is
    const [isWidescreen, setIsWidescreen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    //refs for aligning the vertical tool bar on widescreens
    const gardenGridRef = useRef(null);
    const controlsRef = useRef(null);
    const [gridTop, setGridTop] = useState(0);

    // for showing the tutorial/help popup
    const [showTutorial, setShowTutorial] = useState(false);


    // db needed auth
    const {user} = useAuth();
    const [guestUserCheck, setGuestUserCheck] = useState(false);


    //checkign if on mobile to change the grid inputs (NOW RECHECKS WHEN THE WINDOW RESIZES!!)
    useEffect(() => {
        const handleResize = () => {
            const isMobileWidth = window.innerWidth < 768;
            const isWidescreen = window.innerWidth > 770;

            if (isMobileWidth) {
                const mobileWidth = 10;
                const mobileHeight = 15;

                setInputWidth(mobileWidth);
                setInputHeight(mobileHeight);
                setWidth(mobileWidth);
                setHeight(mobileHeight);
                setIsWidescreen(false);
                setIsMobile(true);
            } else if (isWidescreen) {
                const desktopWidth = 25;
                const desktopHeight = 25;

                setInputWidth(desktopWidth);
                setInputHeight(desktopHeight);
                setWidth(desktopWidth);
                setHeight(desktopHeight);
                setIsWidescreen(true);
                setIsMobile(false);
            }
        };

        // Run on mount
        handleResize();

        // Add resize listener
        window.addEventListener('resize', handleResize);

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);



    // updating ref for the vertical toolbar top
    useEffect(() => {
        const updatePosition = () => {
            if (gardenGridRef.current) {
                const top = gardenGridRef.current.getBoundingClientRect().top + window.scrollY;
                setGridTop(top);
            }
        };

        updatePosition(); // Initial call
        window.addEventListener('resize', updatePosition); // Update on resize
        return () => window.removeEventListener('resize', updatePosition); // Cleanup
    }, []);

    // const for differentiating between screens sizes
    const controlsProps = isWidescreen
        ? { ref: controlsRef, style: { top: `${gridTop}px`} }
        : {};


    useEffect(() => {
        if (activeTool === 'move') {
            setShowMoveControls(true);
        } else {
            // Add a short delay before hiding to allow for the animation
            setTimeout(() => {
                setShowMoveControls(false);
            }, 300); // Match this to your animation duration
        }
    }, [activeTool]);

    useEffect(() => {
        setGridColors(Array(width * height).fill(false));
    }, [width, height]);

    const toggleTool = (tool) => {
        if (tool === 'denote') {
            setDenoteActive((prev) => !prev);
        } else {
            setDenoteActive(false);
        }

        // Clear selected cells if moving, etc.
        if(activeTool === 'move') {
            setSelectedCells([]);
        }
        setActiveTool(tool);
    };

    const clearGrid = () => {
        if (activeTool) {
            setGridColors((prevGrid) =>
                prevGrid.map((cell, i) => false) // Clears all cells
            );
            setActiveTool('add');
        }
    }

    const fillGrid = (index) => {
        const currentState = gridColors[index]; // Get the state of the clicked cell
        if (activeTool !== 'fill' || gridColors[index] === true) return; // Prevent filling if already filled

        const newGrid = [...gridColors];
        const queue = [index]; // Use a queue for BFS (or stack for DFS)

        while (queue.length > 0) {
            const curr = queue.shift(); // Take the first element
            if (newGrid[curr] !== currentState) continue; // Skip if it's already filled differently

            newGrid[curr] = true; // Fill the current cell

            // Get adjacent cells (left, right, top, bottom)
            const neighbors = [];
            if (curr % width !== 0) neighbors.push(curr - 1); // Left
            if ((curr + 1) % width !== 0) neighbors.push(curr + 1); // Right
            if (curr >= width) neighbors.push(curr - width); // Top
            if (curr < gridColors.length - width) neighbors.push(curr + width); // Bottom

            // Add only valid neighbors to the queue
            neighbors.forEach((n) => {
                if (newGrid[n] === currentState) queue.push(n);
            });
        }

        setGridColors(newGrid); // Update the grid
    };

    const clearSelected = (index) => {
        const currentState = gridColors[index]; // Get the state of the clicked cell
        if (activeTool !== 'delete' || gridColors[index] === false) return; // Prevent clearing if already empty

        const newGrid = [...gridColors];
        const queue = [index]; // Use a queue for BFS (or stack for DFS)

        while (queue.length > 0) {
            const curr = queue.shift(); // Take the first element
            if (newGrid[curr] !== currentState) continue; // Skip if it's already filled differently

            newGrid[curr] = false; // Fill the current cell

            // Get adjacent cells (left, right, top, bottom)
            const neighbors = [];
            if (curr % width !== 0) neighbors.push(curr - 1); // Left
            if ((curr + 1) % width !== 0) neighbors.push(curr + 1); // Right
            if (curr >= width) neighbors.push(curr - width); // Top
            if (curr < gridColors.length - width) neighbors.push(curr + width); // Bottom

            // Add only valid neighbors to the queue
            neighbors.forEach((n) => {
                if (newGrid[n] === currentState) queue.push(n);
            });
        }

        setGridColors(newGrid); // Update the grid
    };

    const toggleColor = (index) => {
        if (activeTool === 'add') {
            setGridColors((prevGrid) =>
                prevGrid.map((cell, i) => (i === index ? true : cell)) // Ensures it turns on only
            );
        } else if (activeTool === 'remove') {
            setGridColors((prevGrid) =>
                prevGrid.map((cell, i) => (i === index ? false : cell)) // Ensures it turns off only
            );
        } else if (activeTool === 'move') {
            if (isCellDenoted(index)) {
                return;
            }
            // Perform flood fill to select all connected cells
            floodFill(index);
        }
    };


    const handleMouseDown = (index) => {
        setIsDragging(true);
        if (activeTool === 'denote') {
            handleDenoting(index);
        } else if (activeTool === 'fill') {
            fillGrid(index); // Call fill function
        } else if (activeTool === 'delete') {
            clearSelected(index); // Call delete function
        } else {
            toggleColor(index); // Toggle color for add/remove or select for move
        }
    };

    const handleMouseEnter = (index) => {
        if (isDragging) {
            toggleColor(index);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const floodFill = (startIndex) => {
        const newGrid = [...gridColors];
        const currentState = gridColors[startIndex];

        if (currentState === false) return; // Skip if the clicked cell is empty

        const selected = new Set();
        const visited = new Set();

        // Use an array as a queue, but with pointers instead of shift()
        const queue = [startIndex];
        let queueStart = 0;

        while (queueStart < queue.length) {
            const curr = queue[queueStart++]; // Dequeue without shifting

            if (visited.has(curr) || newGrid[curr] !== currentState) continue;

            visited.add(curr);
            newGrid[curr] = true;
            selected.add(curr);

            // Get adjacent cells (left, right, top, bottom)
            if (curr % width !== 0 && !visited.has(curr - 1)) queue.push(curr - 1); // Left
            if ((curr + 1) % width !== 0 && !visited.has(curr + 1)) queue.push(curr + 1); // Right
            if (curr >= width && !visited.has(curr - width)) queue.push(curr - width); // Top
            if (curr < gridColors.length - width && !visited.has(curr + width)) queue.push(curr + width); // Bottom
        }

        setSelectedCells(Array.from(selected)); // Convert back to array if needed
        setGridColors(newGrid);
    };

    const handleDenoting = (index) => {
        // Perform flood fill to select all connected cells immediately
        const selectedGroup = floodFillWithReturn(index);

        // If the selected group is empty, return
        if (selectedGroup.length === 0) return;

        // Check if this group has already been denoted by comparing arrays
        const isAlreadyDenoted = Object.values(denotedPlots).some(plotArray => {
            // Compare if arrays have same elements (regardless of order)
            if (plotArray.length !== selectedGroup.length) return false;
            const sortedGroup = [...selectedGroup].sort((a, b) => a - b);
            const sortedPlot = [...plotArray].sort((a, b) => a - b);
            return sortedGroup.every((val, idx) => val === sortedPlot[idx]);
        });

        if (isAlreadyDenoted) {
            alert('This plot has already been denoted.');
            return;
        }

        // Start naming process for the current plot
        setCurrentPlot(selectedGroup.join('-'));  // Still store as string for the modal
        setIsNaming(true);
    };

// Create a new helper function that returns the selected group
    const floodFillWithReturn = (startIndex) => {
        const newGrid = [...gridColors];
        const currentState = gridColors[startIndex];

        if (currentState === false) return []; // Skip if the clicked cell is empty

        const selected = new Set();
        const visited = new Set();

        const queue = [startIndex];
        let queueStart = 0;

        while (queueStart < queue.length) {
            const curr = queue[queueStart++];

            if (visited.has(curr) || newGrid[curr] !== currentState) continue;

            visited.add(curr);
            newGrid[curr] = true;
            selected.add(curr);

            // Get adjacent cells (left, right, top, bottom)
            if (curr % width !== 0 && !visited.has(curr - 1)) queue.push(curr - 1); // Left
            if ((curr + 1) % width !== 0 && !visited.has(curr + 1)) queue.push(curr + 1); // Right
            if (curr >= width && !visited.has(curr - width)) queue.push(curr - width); // Top
            if (curr < gridColors.length - width && !visited.has(curr + width)) queue.push(curr + width); // Bottom
        }

        // Update the grid and selected cells in one go
        setGridColors(newGrid);
        setSelectedCells(Array.from(selected));

        return Array.from(selected);
    };

    const sanitizeGardenName = (name) => {
        if (!name || typeof name !== 'string') {
            return '';
        }

        return name
            .trim() // Remove leading/trailing whitespace
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/[<>\"'&]/g, '') // Remove potentially dangerous HTML characters
            .replace(/[/\\:*?"<>|]/g, '') // Remove characters that could cause file system issues
            .substring(0, 50); // Limit length to prevent overly long names
    };

    const validateGardenName = (name) => {
        const sanitized = sanitizeGardenName(name);

        if (!sanitized) {
            return { isValid: false, message: 'Garden name cannot be empty.' };
        }

        if (sanitized.length < 2) {
            return { isValid: false, message: 'Garden name must be at least 2 characters long.' };
        }

        // Check for reserved names or patterns
        const reservedNames = ['con', 'prn', 'aux', 'nul', 'com1', 'lpt1'];
        if (reservedNames.includes(sanitized.toLowerCase())) {
            return { isValid: false, message: 'This name is reserved. Please choose a different name.' };
        }

        return { isValid: true, sanitized };
    };

    const saveGardenName = () => {
        const validation = validateGardenName(gardenName);

        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        const sanitizedName = validation.sanitized;

        // Check if name already exists (using sanitized name for comparison)
        if (Object.keys(denotedPlots).some(key =>
            sanitizeGardenName(denotedPlots[key]).toLowerCase() === sanitizedName.toLowerCase()
        )) {
            alert('Garden name already exists. Please choose a different name.');
            return;
        }

        // Convert currentPlot string to array of numbers
        const plotArray = currentPlot.split('-').map(Number);

        // Store garden name as key and plot array as value (using sanitized name)
        setDenotedPlots((prev) => ({
            ...prev,
            [sanitizedName]: plotArray
        }));

        // Reset naming process
        setIsNaming(false);
        setCurrentPlot(null);
        setGardenName('');
    };

    const isCellDenoted = (index) => {
        return Object.values(denotedPlots).some(plotArray =>
            plotArray.includes(index)
        );
    };


    const moveCells = (direction) => {
        // Create a copy of the grid
        const newGridColors = [...gridColors];

        // First, clear the old positions of the selected group
        selectedCells.forEach((index) => {
            newGridColors[index] = false;
        });

        // Prepare a new array to track the updated selected group positions
        const newSelectedCells = [];

        // For each cell in the group, compute its new index based on the direction
        selectedCells.forEach((index) => {
            const neighbors = getNeighbors(index);
            let newIndex;

            switch (direction) {
                case 'up':
                    newIndex = neighbors.top;
                    break;
                case 'down':
                    newIndex = neighbors.bottom;
                    break;
                case 'left':
                    newIndex = neighbors.left;
                    break;
                case 'right':
                    newIndex = neighbors.right;
                    break;
                default:
                    return;
            }

            // Move the cell if the new position is within bounds and currently empty
            if (newIndex !== undefined && newGridColors[newIndex] === false) {
                newGridColors[newIndex] = true;
                newSelectedCells.push(newIndex);
            }
        });

        // Update the grid and the selected group positions
        setGridColors(newGridColors);
        setSelectedCells(newSelectedCells);
    };


    const getNeighbors = (index) => {
        const neighbors = {};
        const row = Math.floor(index / width);
        const col = index % width;

        // Determine neighbors
        if (row > 0) neighbors.top = index - width; // Top
        if (row < height - 1) neighbors.bottom = index + width; // Bottom
        if (col > 0) neighbors.left = index - 1; // Left
        if (col < width - 1) neighbors.right = index + 1; // Right

        return neighbors;
    };

    // function to handle resizing of the grid
    const handleResize = () => {
        const newWidth = inputWidth === '' ? 25 : inputWidth;
        const newHeight = inputHeight === '' ? 25 : inputHeight;

        setWidth(newWidth);
        setHeight(newHeight);
    };

    const handleDeleteGarden = (gardenName) => {
        // Create a copy of the current denotedPlots and remove the specified garden
        const updatedPlots = { ...denotedPlots };
        delete updatedPlots[gardenName];
        setDenotedPlots(updatedPlots);
    };

    const handleSavingGarden = (gardenName, matrix, soilType, hardiness, sunlight, maintenance) => {
        setSavedGardenPlots((prev) => ({
            ...prev,
            [gardenName]: {
                matrix,
                soilType,
                hardiness,
                sunlight,
                maintenance,
            }
        }));
    }

    const handleRotatingGarden = (gardenName, rotatedMatrix) => {
        // Find the plot key in denotedPlots that matches the garden name
        const plotKeyToRotate = Object.entries(denotedPlots).find(
            ([_, name]) => name === gardenName
        )?.[0];

        if (!plotKeyToRotate) return;

        // Convert the matrix back to plot numbers
        const rotatedPlots = [];
        const matrixRows = rotatedMatrix.split('\n');

        for (let i = 0; i < matrixRows.length; i++) {
            const row = matrixRows[i].split(',').map(Number);
            for (let j = 0; j < row.length; j++) {
                if (row[j] === 1) {
                    rotatedPlots.push(i * inputWidth + j);
                }
            }
        }

        // Update denotedPlots with the new plot configuration
        const sortedRotatedPlots = rotatedPlots.sort((a, b) => a - b);
        const newPlotKey = sortedRotatedPlots.join('-');

        // Create a new entry with the rotated plots and remove the old one
        setDenotedPlots(prev => {
            const updated = { ...prev };
            delete updated[plotKeyToRotate];
            updated[newPlotKey] = gardenName;
            return updated;
        });

        // Also update saved garden plots if needed
        if (savedGardenPlots[gardenName]) {
            setSavedGardenPlots(prev => ({
                ...prev,
                [gardenName]: {
                    ...prev[gardenName],
                    matrix: rotatedMatrix
                }
            }));
        }
    };

    const handleDuplicateSaveName = () => {
        const validation = validateGardenName(gardenName);

        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        const sanitizedName = validation.sanitized;

        // Check if name already exists (using sanitized name for comparison)
        if (Object.keys(denotedPlots).some(key =>
            sanitizeGardenName(denotedPlots[key]).toLowerCase() === sanitizedName.toLowerCase()
        )) {
            alert('Garden name already exists. Please choose a different name.');
            return;
        }

        // Parse the currentPlot string back to array
        const plotArray = currentPlot.split('-').map(Number);

        // Save the denoted plot with the new name (using sanitized name)
        setDenotedPlots((prev) => ({
            ...prev,
            [sanitizedName]: plotArray,
        }));

        // Reset naming process
        setIsNamingDuplicatedGarden(false);
        setCurrentPlot(null);
        setGardenName('');
    };

    const handleDuplicateClick = (gardenName) => {
        if (!denotedPlots[gardenName]) {
            console.error("Garden not found for duplication");
            return;
        }

        // Set current plot to be the array from the existing garden
        setCurrentPlot(denotedPlots[gardenName].join('-'));
        setIsNamingDuplicatedGarden(true);
    };

    // Navigate to a new page to add veggies to the garden!
    const handleNextSteps = () => {
        navigate('/garden-summary', { state: { denotedPlots, savedGardenPlots } });
    }

    const handleTutorialOpen = () => {
        // Optionally clear any active tool state when opening tutorial
        setActiveTool('add'); // Reset to default
        setShowTutorial(true);
    };

    return (
        <div className="garden-layout" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
            <RibbonNav pageName={"Grid Designer"}/>
            {/*<h2 className={"in-ground-designer-title"}>In Ground Garden Designer</h2>*/}

            <div className={"page-layout-manager-container"}>

                <div className={"in-ground-garden-grid-wrapper"}>

                    <div className={"inGround-garden-page-split"}>
                        <div className="inGround-garden-page-controls" {...controlsProps}>
                            {isMobile && (
                                <div className={"dimension-inputs"}>
                                    <CollapsibleSection
                                        title="Dimensions"
                                        icon={Grid3x3}
                                        iconColor="text-emerald-200"
                                        defaultExpanded={false}
                                    >
                                        <div className="input-group">
                                            <label> Width: </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="50"
                                                value={inputWidth}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Only convert to Number if the value isn't empty
                                                    setInputWidth(value === '' ? '' : Number(value));
                                                }}
                                            />
                                            <label> ft </label>
                                        </div>

                                        <div className="input-group">
                                            <label> Height: </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="50"
                                                value={inputHeight}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setInputHeight(value === '' ? '' : Number(value));
                                                }}
                                            />
                                            <label> ft </label>
                                        </div>

                                        <button className="basic-button-clickable" onClick={handleResize}>Resize
                                        </button>
                                    </CollapsibleSection>

                                </div>
                            )}

                            <div className={"vertical-button-section"}>
                                <div className="tool-bar-vertical">
                                    <button
                                        onClick={() => navigate("/")}
                                        className="basic-button-clickable"
                                    >
                                        Back
                                    </button>
                                    {/*<button*/}
                                    {/*    onClick={() => navigate("/")}*/}
                                    {/*    className="basic-button-clickable-icon"*/}
                                    {/*>*/}
                                    {/*    <ArrowLeft size={30} strokeWidth={2} color="#f3f3f3"*/}
                                    {/*               className={"garden-style-dropdown-icon"}/>*/}
                                    {/*</button>*/}
                                    <button
                                        title={"Add"}
                                        onClick={() => toggleTool('add')}
                                        className={`basic-button-clickable-icon ${activeTool === 'add' ? 'selected' : ''}`}
                                    >
                                        <PencilLine size={30} strokeWidth={2} color="#f3f3f3"
                                                    className={"garden-style-dropdown-icon"}/>
                                    </button>
                                    <button
                                        title={"Erase"}
                                        onClick={() => toggleTool('remove')}
                                        className={`basic-button-clickable-icon ${activeTool === 'remove' ? 'selected' : ''}`}
                                    >
                                        <Eraser size={30} strokeWidth={2} color="#f3f3f3"
                                                className={"garden-style-dropdown-icon"}/>
                                    </button>
                                    <button
                                        title={"Fill"}
                                        onClick={() => toggleTool('fill')}
                                        className={`basic-button-clickable-icon ${activeTool === 'fill' ? 'selected' : ''}`}
                                    >
                                        <PaintBucket size={30} strokeWidth={2} color="#f3f3f3"
                                                     className={"garden-style-dropdown-icon"}/>
                                    </button>
                                    <button
                                        title={"Delete"}
                                        onClick={() => toggleTool('delete')}
                                        className={`basic-button-clickable-icon ${activeTool === 'delete' ? 'selected' : ''}`}
                                    >
                                        <X size={30} strokeWidth={2} color="#f3f3f3"
                                           className={"garden-style-dropdown-icon"}/>
                                    </button>
                                    {/*<button*/}
                                    {/*    onClick={() => toggleTool('move')}*/}
                                    {/*    className={`basic-button-clickable-icon ${activeTool === 'move' ? 'selected' : ''}`}*/}
                                    {/*>*/}
                                    {/*    <Move size={30} strokeWidth={2} color="#f3f3f3"*/}
                                    {/*          className={"garden-style-dropdown-icon"}/>*/}
                                    {/*</button>*/}
                                    <button
                                        title={"Clear the Grid"}
                                        onClick={() => clearGrid()}
                                        className={`basic-button-clickable-icon ${activeTool === 'clear' ? 'selected' : ''}`}
                                    >
                                        <Trash size={30} strokeWidth={2} color="#f3f3f3"
                                               className={"garden-style-dropdown-icon"}/>
                                    </button>
                                    <button
                                        title={"Denote Gardens"}
                                        onClick={() => toggleTool('denote')}
                                        className={`basic-button-clickable-icon ${activeTool === 'denote' ? 'selected' : ''}`}
                                    >
                                        <LayoutDashboard size={30} strokeWidth={2} color="#f3f3f3"
                                                         className={"garden-style-dropdown-icon"}/>
                                    </button>

                                    {isMobile && (
                                        <button
                                            title={"Help"}
                                            onClick={() => handleTutorialOpen()}
                                            className={`basic-button-clickable-icon`}
                                        >
                                            <Info size={30} strokeWidth={2} color="#f3f3f3"
                                                  className={"garden-style-dropdown-icon"}/>
                                        </button>
                                    )}
                                </div>

                                {/*<div*/}
                                {/*    className={`tool-bar-vertical movement-controls ${activeTool === 'move' ? 'visible' : ''} ${!showMoveControls ? 'hidden' : ''}`}>*/}
                                {/*    <button*/}
                                {/*        onClick={() => moveCells('up')}*/}
                                {/*        disabled={selectedCells.length === 0}*/}
                                {/*        className="basic-button-clickable-icon"*/}
                                {/*    >*/}
                                {/*        <ChevronsUp size={30} strokeWidth={2} color="#f3f3f3"*/}
                                {/*                    className={"garden-style-dropdown-icon"}/>*/}
                                {/*    </button>*/}
                                {/*    <button*/}
                                {/*        onClick={() => moveCells('down')}*/}
                                {/*        disabled={selectedCells.length === 0}*/}
                                {/*        className="basic-button-clickable-icon"*/}
                                {/*    >*/}
                                {/*        <ChevronsDown size={30} strokeWidth={2} color="#f3f3f3"*/}
                                {/*                      className={"garden-style-dropdown-icon"}/>*/}
                                {/*    </button>*/}
                                {/*    <button*/}
                                {/*        onClick={() => moveCells('left')}*/}
                                {/*        disabled={selectedCells.length === 0}*/}
                                {/*        className="basic-button-clickable-icon"*/}
                                {/*    >*/}
                                {/*        <ChevronsLeft size={30} strokeWidth={2} color="#f3f3f3"*/}
                                {/*                      className={"garden-style-dropdown-icon"}/>*/}
                                {/*    </button>*/}
                                {/*    <button*/}
                                {/*        onClick={() => moveCells('right')}*/}
                                {/*        disabled={selectedCells.length === 0}*/}
                                {/*        className="basic-button-clickable-icon"*/}
                                {/*    >*/}
                                {/*        <ChevronsRight size={30} strokeWidth={2} color="#f3f3f3"*/}
                                {/*                       className={"garden-style-dropdown-icon"}/>*/}
                                {/*    </button>*/}
                                {/*</div>*/}
                            </div>

                            {/*<div className="tool-bar-mini">*/}
                            {/*    <button className="basic-button-clickable" onClick={() => navigate("/")}>Back</button>*/}
                            {/*</div>*/}
                        </div>

                        <div className={"last-chance-horz-container"}>
                            {isWidescreen && (
                                <div className={"horizontal-dimension-inputs-wrapper"}>
                                    <button
                                        title={"Help"}
                                        className={`help-button`}
                                        onClick={() => handleTutorialOpen()}
                                    >
                                        <Info size={35} strokeWidth={3} color="#f3f3f3"
                                           className={"help-button-styling"}/>
                                    </button>

                                    <HorizontalCollapsibleSection
                                        title="Dimensions"
                                        icon={Grid3x3}
                                    >
                                        <div className={"horizontal-dimension-inputs"}>
                                            <div className="horz-input-group">
                                                <label> Width: </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="50"
                                                    value={inputWidth}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Only convert to Number if the value isn't empty
                                                        setInputWidth(value === '' ? '' : Number(value));
                                                    }}
                                                />
                                                <label> ft </label>
                                            </div>

                                            <div className="horz-input-group">
                                                <label> Height: </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="50"
                                                    value={inputHeight}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setInputHeight(value === '' ? '' : Number(value));
                                                    }}
                                                />
                                                <label> ft </label>
                                            </div>

                                            <button className="basic-button-clickable-no-padding"
                                                    onClick={handleResize}>Resize
                                            </button>
                                        </div>
                                    </HorizontalCollapsibleSection>
                                </div>
                            )}

                            <div className="inGround-garden-grid-wrapper">
                                <div id={"garden-grid-container"}
                                     ref={gardenGridRef}
                                     className={`garden-grid-container ${denoteActive ? '' : ''}`}>
                                    <div
                                        className={`garden-grid ${denoteActive ? '' : ''}`}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: `repeat(${width}, 25px)`,
                                            gridTemplateRows: `repeat(${height}, 25px)`,
                                        }}
                                    >
                                        {gridColors.map((isToggled, index) => (
                                            <button
                                                key={index}
                                                onMouseDown={() => handleMouseDown(index)}
                                                onMouseEnter={() => handleMouseEnter(index)}
                                                className={`
                                ${gridColors[index] && activeTool !== 'denote' ? 'toggled' : ''}
                                ${activeTool === 'add' ? 'add-active' : ''}
                                ${activeTool === 'move' ? 'move-active' : ''}
                                ${activeTool === 'move' && selectedCells.includes(index) ? 'moving-toggle' : ''}
                                ${activeTool === 'remove' ? 'remove-active' : ''}
                                ${activeTool === 'fill' ? 'fill-active' : ''}
                                ${activeTool === 'delete' ? 'delete-active' : ''}
                                ${activeTool === 'denote' ? 'denote-active' : ''}
                                ${activeTool === 'denote' && gridColors[index] ? 'denote-toggled' : ''}
                                ${isCellDenoted(index) ? 'denoted-plot' : ''}
                            `}
                                            />
                                        ))}
                                    </div>
                                    <div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* the empty '' were used to change to the split denoted screen w/ grid but its below it now */}
                    </div>
                </div>

                <DenotedGardenSectionMenu denoteActive={true} denotedPlots={denotedPlots}
                                          inputWidth={inputWidth} inputHeight={inputHeight}
                                          onDeleteGarden={handleDeleteGarden}
                                          onSave={handleSavingGarden}
                                          onRotate={handleRotatingGarden}
                                          onDuplicate={handleDuplicateClick}
                                          onNextSteps={handleNextSteps}
                                          savedGardenPlots={savedGardenPlots}
                />

            </div>

            {(!user && !guestUserCheck) && (
                <div className={"garden-guest-user-warning-modal-background-container"}>
                    <div className="garden-guest-user-warning-modal">
                        <h3 className={"garden-naming-guest-user-warning-modal-title"}>Warning! Guest User Detected!</h3>
                        <p>
                            As you are not logged in, guest users cannot save garden layouts, but still have full
                            access to all tools currently provided. Please log in if you wish to save your garden
                            for future use!
                        </p>
                        <button onClick={() => setGuestUserCheck(true)}>Got it!</button>
                    </div>
                </div>
            )}

            {isNaming && (
                <div className={"garden-naming-modal-background-container"}>
                    <div className="garden-naming-modal">
                        <h3 className={"garden-naming-modal-title"}>Name Your Garden Plot</h3>
                        <input
                            type="text"
                            value={gardenName}
                            onChange={(e) => setGardenName(e.target.value)}
                            placeholder="Enter garden name"
                        />
                        <button onClick={saveGardenName}>Save</button>
                        <button onClick={() => setIsNaming(false)}>Cancel</button>
                    </div>
                </div>
            )}

            {isNamingDuplicatedGarden && (
                <div className={"garden-naming-modal-background-container"}>
                    <div className="garden-naming-modal">
                        <h3 className={"garden-naming-modal-title"}>Name Duplicated Plot</h3>
                        <input
                            type="text"
                            value={gardenName}
                            onChange={(e) => setGardenName(e.target.value)}
                            placeholder="Enter garden name"
                        />
                        <button onClick={handleDuplicateSaveName}>Save</button>
                        <button onClick={() => setIsNamingDuplicatedGarden(false)}>Cancel</button>
                    </div>
                </div>
            )}

            {showTutorial && (
                <Tutorial
                    screens={[
                        <MainPageTutorialFirst />,
                        <MainPageTutorialSecond />
                    ]}
                    isOpen={true}
                    onClose={() => setShowTutorial(false)}
                    onFinish={() => setShowTutorial(false)}
                />
            )}

        </div>
    );
};

export default InGroundGardenDesigner;
